import React, { useState } from "react";
import {
  Box,
  Drawer,
  Typography,
  IconButton,
  Divider,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import InventoryOutlinedIcon from "@mui/icons-material/InventoryOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import MeetingRoomOutlinedIcon from "@mui/icons-material/MeetingRoomOutlined";

const NOTIFICATIONS = [
  {
    id: 1,
    type: "order",
    title: "New order received",
    description: "Order ORD-982318 is ready for picking.",
    time: "5 min ago",
    date: "Today",
    icon: ShoppingCartOutlinedIcon,
    iconBg: "rgba(0, 94, 255, 0.10)",
    iconColor: "#005EFF",
    actions: [{ label: "View", link: "#" }, { label: "Start Picking", link: "#" }],
    unread: true,
  },
  {
    id: 2,
    type: "picking",
    title: "Picking completed",
    description: "Picking for ORD-982318 is done for packing.",
    time: "30 min ago",
    date: "Today",
    icon: ShoppingCartOutlinedIcon,
    iconBg: "rgba(0, 94, 255, 0.10)",
    iconColor: "#005EFF",
    actions: [{ label: "View", link: "#" }, { label: "Start Packing", link: "#" }],
    unread: false,
  },
  {
    id: 3,
    type: "stock",
    title: "Stock Alert",
    description: "Item SKU-001 reached the threshold.",
    time: "15 min ago",
    date: "Today",
    icon: WarningAmberOutlinedIcon,
    iconBg: "rgba(255, 59, 48, 0.10)",
    iconColor: "#FF3B30",
    actions: [{ label: "View", link: "#" }],
    unread: false,
  },
  {
    id: 4,
    type: "item",
    title: "Item is onboarded",
    description: "Item SKU-001-2 waiting for Bin Placement.",
    time: "20 min ago",
    date: "Today",
    icon: InventoryOutlinedIcon,
    iconBg: "rgba(52, 199, 89, 0.10)",
    iconColor: "#34C759",
    actions: [{ label: "View", link: "#" }],
    unread: false,
  },
  {
    id: 5,
    type: "dispatch",
    title: "Package missed dispatch",
    description: "1 package was not dispatched with the Van No.",
    time: "25 min ago",
    date: "Today",
    icon: LocalShippingOutlinedIcon,
    iconBg: "rgba(255, 149, 0, 0.10)",
    iconColor: "#FF9500",
    actions: [{ label: "View", link: "#" }, { label: "Reassign", link: "#" }],
    unread: true,
  },
  {
    id: 6,
    type: "room",
    title: "Room almost full",
    description: "Room 1 is at 90% capacity.",
    time: "40 min ago",
    date: "Today",
    icon: MeetingRoomOutlinedIcon,
    iconBg: "rgba(138, 43, 226, 0.10)",
    iconColor: "#8A2BE2",
    actions: [{ label: "View", link: "#" }],
    unread: false,
  },
  {
    id: 7,
    type: "stock",
    title: "Stock Alert",
    description: "Item SKU-001 reached the threshold.",
    time: "5 min ago",
    date: "Yesterday",
    icon: WarningAmberOutlinedIcon,
    iconBg: "rgba(255, 59, 48, 0.10)",
    iconColor: "#FF3B30",
    actions: [{ label: "View", link: "#" }],
    unread: false,
  },
  {
    id: 8,
    type: "item",
    title: "Item is onboarded",
    description: "Item SKU-001-2 waiting for Bin Placement.",
    time: "20 min ago",
    date: "Yesterday",
    icon: InventoryOutlinedIcon,
    iconBg: "rgba(52, 199, 89, 0.10)",
    iconColor: "#34C759",
    actions: [{ label: "View", link: "#" }],
    unread: true,
  },
  {
    id: 9,
    type: "order",
    title: "New order received",
    description: "Order ORD-982318 is ready for picking.",
    time: "5 min ago",
    date: "Yesterday",
    icon: ShoppingCartOutlinedIcon,
    iconBg: "rgba(0, 94, 255, 0.10)",
    iconColor: "#005EFF",
    actions: [{ label: "View", link: "#" }, { label: "Start Picking", link: "#" }],
    unread: false,
  },
  {
    id: 10,
    type: "picking",
    title: "Picking completed",
    description: "Picking for ORD-982318 is done for packing.",
    time: "30 min ago",
    date: "Yesterday",
    icon: ShoppingCartOutlinedIcon,
    iconBg: "rgba(0, 94, 255, 0.10)",
    iconColor: "#005EFF",
    actions: [{ label: "View", link: "#" }, { label: "Start Packing", link: "#" }],
    unread: true,
  },
  {
    id: 11,
    type: "dispatch",
    title: "Package missed dispatch",
    description: "1 package was not dispatched with the Van No.",
    time: "25 min ago",
    date: "Yesterday",
    icon: LocalShippingOutlinedIcon,
    iconBg: "rgba(255, 149, 0, 0.10)",
    iconColor: "#FF9500",
    actions: [{ label: "View", link: "#" }, { label: "Reassign", link: "#" }],
    unread: false,
  },
  {
    id: 12,
    type: "room",
    title: "Room almost full",
    description: "Room 1 is at 90% capacity.",
    time: "40 min ago",
    date: "Yesterday",
    icon: MeetingRoomOutlinedIcon,
    iconBg: "rgba(138, 43, 226, 0.10)",
    iconColor: "#8A2BE2",
    actions: [{ label: "View", link: "#" }],
    unread: false,
  },
];

interface NotificationsProps {
  open: boolean;
  onClose: () => void;
}

const Notifications = ({ open, onClose }: NotificationsProps)=> {
  const [activeTab, setActiveTab] = useState<"all" | "unread" | "read">("all");
  const [notifications, setNotifications] = useState(NOTIFICATIONS);

  const handleMarkAllAsRead = () => {
    setNotifications(notifications.map(n => ({ ...n, unread: false })));
  };

  const filteredNotifications = notifications.filter(n => {
    if (activeTab === "unread") return n.unread;
    if (activeTab === "read") return !n.unread;
    return true;
  });

  const groupedNotifications = filteredNotifications.reduce((acc, notif) => {
    if (!acc[notif.date]) acc[notif.date] = [];
    acc[notif.date].push(notif);
    return acc;
  }, {} as Record<string, typeof NOTIFICATIONS>);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: "400px",
          bgcolor: "background.default",
        },
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        {/* Header */}
        <Box sx={{ p: 2, bgcolor: "background.paper" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography sx={{ fontSize: "20px", fontWeight: 600, color: "text.primary" }}>Notifications</Typography>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>

          <Divider sx={{ mb: 2 }} />

          {/* Tabs */}
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <Box sx={{ display: "flex", gap: 1, flex: 1 }}>
              {([
                { key: "all", label: "All" },
                { key: "unread", label: "Unread" },
                { key: "read", label: "Read" },
              ] as const).map((tab) => (
                <Button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  disableRipple
                  sx={{
                    textTransform: "capitalize",
                    borderRadius: "20px",
                    border: "1px solid",
                    borderColor: "grey.100",
                    bgcolor: activeTab === tab.key ? "primary.main" : "#fff",
                    color: activeTab === tab.key ? "#fff" : "text.primary",
                    fontSize: "14px",
                    fontWeight: 500,
                    py: 0.5,
                    px: 2.5,
                    minWidth: "auto",
                    "&:hover": {
                      bgcolor: activeTab === tab.key ? "primary.main" : "#fff",
                    },
                  }}
                >
                  {tab.label}
                </Button>
              ))}
            </Box>
            <Typography 
              onClick={handleMarkAllAsRead}
              sx={{ fontSize: "16px", color: "primary.main", cursor: "pointer", fontWeight: 400, whiteSpace: "nowrap" }}
            >
              Mark all as read
            </Typography>
          </Box>
        </Box>

        {/* Notifications List */}
        <Box
          sx={{
            flex: 1,
            overflow: "auto",
            "&::-webkit-scrollbar": { display: "none" },
            msOverflowStyle: "none",
            scrollbarWidth: "none",
          }}
        >
          {Object.entries(groupedNotifications).map(([date, notifications]) => (
            <Box key={date}>
              <Typography
                sx={{
                  px: 2,
                  py: 1,
                  fontSize: "20px",
                  fontWeight: 600,
                  color: "text.primary",
                  bgcolor: "background.default",
                  position: "sticky",
                  top: 0,
                  zIndex: 1,
                }}
              >
                {date}
              </Typography>

              {notifications.map((notif) => {
                const Icon = notif.icon;
                return (
                  <Box
                    key={notif.id}
                    sx={{
                      p: 2,
                      bgcolor: notif.unread ? "primary.light" : "background.default",
                      borderBottom: "1px solid",
                      borderColor: "grey.100",
                    }}
                  >
                    <Box sx={{ display: "flex", gap: 1.5 }}>
                      <Box
                        sx={{
                          width: "40px",
                          height: "40px",
                          borderRadius: "8px",
                          bgcolor: notif.iconBg,
                          border: "1px solid rgba(74, 85, 101, 0.10)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <Icon sx={{ fontSize: "20px", color: notif.iconColor }} />
                      </Box>

                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start"}}>
                          <Typography sx={{ fontSize: "16px", fontWeight: 700, color: "text.primary" }}>
                            {notif.title}
                          </Typography>
                          <Typography sx={{ fontSize: "10px",fontWeight: 400, color: "grey.700", whiteSpace: "nowrap", ml: 1 }}>
                            {notif.time}
                          </Typography>
                        </Box>

                        <Typography sx={{ fontSize: "12px",fontWeight: 400, color: "grey.700" }}>
                          {notif.description}
                        </Typography>

                        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                          {notif.actions.map((action, idx) => (
                            <Typography
                              key={idx}
                              sx={{
                                fontSize: "14px",
                                color: "primary.main",
                                cursor: "pointer",
                                fontWeight: 500,
                                whiteSpace: "nowrap",
                              }}
                            >
                              {action.label}
                            </Typography>
                          ))}
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          ))}
        </Box>
      </Box>
    </Drawer>
  );
};
export default Notifications;
